import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { formatDate, parse } from 'date-fns';
import { Navigate } from 'react-router-dom';
import { VoucherContractRechargeType, WithContractGuard } from '@ibk/beneficios';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ContentLayout } from '@ibk/shared-layout';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { DiscountType, FeatureFlagUseCase, ReleaseType } from '@ibk/shared-types';
import { Alert, Loading } from '@ibk/shared-ui';
import { formatCurrency, isConfiguredVoucher } from '@ibk/shared-utils';
import { WizardStepper } from '../../components/WizardStepper';
import { useActiveContractProducts, useBenefitContract, useEmissorBusinessDays, useEmissorContractProducts, useEmissorFlow, useEmissorRepeatOrderInitializer, useHappyHolidaysEndOfTheYearRecharge, useRechargeSimulation, useRepeatRechargeInitializer, } from '../../hooks';
import { benefitApi } from '../../services/benefitApi';
import { beneficiosTranslations } from '../../translations';
import { applyMask, DEFAULT_CURRENCY_VALUE, getUnformatedValue, isActiveStatus } from '../../utils';
import { BeneficiariesValues } from './components/BeneficiariesValues';
import { Competence } from './components/Competence';
import { DeliveryAddress } from './components/DeliveryAddress';
import { EmissorBeneficiariesValues } from './components/EmissorBeneficiariesValues';
import { EmissorDeliveryAddress } from './components/EmissorDeliveryAddress';
import { EmissorSummary } from './components/EmissorSummary';
import { GlobalSettings } from './components/GlobalSettings';
import { getCompetenceString, getDefaultCreditDate } from './components/GlobalSettings/utils';
import { Payment } from './components/Payment';
import { isEmissorProductDiscountEnabled, mapEmissorCategoriesToProductCategories, Products, } from './components/Products';
import { Summary } from './components/Summary';
import { canKeepCardDeliveryDecision, cloneSummaryEditSnapshot, getPreviousRechargeStep, resolveAdvanceStep, shouldSkipDeliveryAddress, } from './hooks/utils/summaryEditNavigation';
import styles from './NewRechargePage.module.css';
import { mapFormDataToSimulateVoucherContractRechargeVoucherRequestDto, NewRechargeStep, SimulationState, } from './types';
const noop = () => undefined;
function NewRechargePageContent() {
    const { isInPeriod } = useHappyHolidaysEndOfTheYearRecharge();
    const [currentStep, setCurrentStep] = useState(NewRechargeStep.COMPETENCE);
    const [alertData, setAlertData] = useState({ visible: false });
    const [isSavingAddress, setIsSavingAddress] = useState(false);
    const [isSimulatingAddress, setIsSimulatingAddress] = useState(false);
    const [isEditingFromSummary, setIsEditingFromSummary] = useState(false);
    const [summaryEditSnapshot, setSummaryEditSnapshot] = useState(null);
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const [updateDeliveryLocation] = benefitApi.useUpdateDeliveryLocationMutation();
    const isBeneficiaryDiscountEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_BENEFICIARY_DISCOUNT, undefined);
    const isRepeatRechargeEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_REPEAT_RECHARGE);
    const { isEmissorFlow, emissorContractId } = useEmissorFlow();
    const { contractData } = useBenefitContract();
    const { products, isLoading: isContractProductsLoading } = useActiveContractProducts(contractData ? { voucherContractId: contractData.id } : { voucherContractId: '' });
    const { products: emissorProducts, categories: emissorCategories, isLoading: isEmissorProductsLoading, } = useEmissorContractProducts({
        contractId: emissorContractId,
        skip: !isEmissorFlow,
    });
    const emissorProductCategories = React.useMemo(() => mapEmissorCategoriesToProductCategories(emissorCategories), [emissorCategories]);
    const { isLoading, isError, formData, handlers, simulate, ensureSimulation = simulate, invalidateSimulationRecord = noop, simulationResult, populateFormData, setInitialFormDataFromRechargeDetail, } = useRechargeSimulation();
    const isDiscountEnabled = React.useMemo(() => !isEmissorFlow || isEmissorProductDiscountEnabled(emissorProducts, formData.voucherTypeId), [isEmissorFlow, emissorProducts, formData.voucherTypeId]);
    const repeatCompetenceDate = React.useMemo(() => formData.competenceDate ?? new Date(), [formData.competenceDate]);
    const repeatCreditDate = React.useMemo(() => getDefaultCreditDate(new Date()), []);
    const { businessDays: emissorBusinessDays, isLoading: isEmissorBusinessDaysLoading } = useEmissorBusinessDays({
        competence: getCompetenceString(repeatCompetenceDate),
        skip: !isEmissorFlow || !isRepeatRechargeEnabled,
    });
    const selectedEmissorProduct = React.useMemo(() => emissorProducts.find(product => product.productId === formData.voucherTypeId), [emissorProducts, formData.voucherTypeId]);
    const globalSettingsProducts = React.useMemo(() => isEmissorFlow
        ? emissorProducts.map(product => ({
            voucherTypeDescription: product.productName ?? product.productId,
            baseValue: product.baseValue,
            amountDaily: product.amountDaily,
        }))
        : products, [isEmissorFlow, emissorProducts, products]);
    const handleRepeatLoadSuccess = useCallback(() => {
        setCurrentStep(NewRechargeStep.SUMMARY);
    }, []);
    const { isInitializing: isLegacyRepeatInitializing, initializationError: legacyRepeatInitializationError } = useRepeatRechargeInitializer({
        enabled: isRepeatRechargeEnabled && !isEmissorFlow,
        contractId: contractData?.id,
        products,
        productsLoading: isContractProductsLoading,
        setInitialFormDataFromRechargeDetail,
        setBeneficiariesData: handlers.setBeneficiariesData,
        simulate,
        populateFormDataFromSimulation: populateFormData,
        onSuccess: handleRepeatLoadSuccess,
    });
    const { isInitializing: isEmissorRepeatInitializing, initializationError: emissorRepeatInitializationError } = useEmissorRepeatOrderInitializer({
        enabled: isRepeatRechargeEnabled && isEmissorFlow,
        contractId: emissorContractId,
        products: emissorProducts,
        productsLoading: isEmissorProductsLoading,
        competenceDate: repeatCompetenceDate,
        creditDate: repeatCreditDate,
        businessDays: emissorBusinessDays,
        businessDaysLoading: isEmissorBusinessDaysLoading,
        setFormData: handlers.setBeneficiariesData,
        onSuccess: handleRepeatLoadSuccess,
    });
    const isRepeatInitializing = isLegacyRepeatInitializing || isEmissorRepeatInitializing;
    const repeatInitializationError = legacyRepeatInitializationError || emissorRepeatInitializationError;
    useEffect(() => {
        if (simulationResult?.vouchers.length) {
            handlers?.selectCreditDate(parse(`${simulationResult.vouchers[0].creditDate}`, 'yyyy-MM-dd', new Date()));
        }
    }, [simulationResult, handlers?.selectCreditDate]);
    const buildSimulationRequest = useCallback(() => {
        const product = products.find(product => product.voucherTypeDescription === formData.voucherType);
        const voucher = mapFormDataToSimulateVoucherContractRechargeVoucherRequestDto(formData, product, isBeneficiaryDiscountEnabled);
        const accrualPeriod = formData.competenceDate ? formatDate(formData.competenceDate, 'yyyy-MM') : undefined;
        return { vouchers: [voucher], accrualPeriod };
    }, [formData, products, isBeneficiaryDiscountEnabled]);
    const handleSimulate = useCallback(async () => {
        if (!contractData?.id) {
            console.error('Contract not found');
            return;
        }
        return await simulate(contractData.id, buildSimulationRequest());
    }, [simulate, contractData?.id, buildSimulationRequest]);
    const handleEnsureSimulation = useCallback(async () => {
        if (!contractData?.id) {
            console.error('Contract not found');
            return;
        }
        return await ensureSimulation(contractData.id, buildSimulationRequest());
    }, [ensureSimulation, contractData?.id, buildSimulationRequest]);
    const prepareSimulationForNavigation = useCallback(async (forcePopulate) => {
        const isLeavingGlobalSettingsWithoutSelection = currentStep === NewRechargeStep.GLOBAL_SETTINGS && !formData.selectedBeneficiariesList?.length;
        if (isLeavingGlobalSettingsWithoutSelection) {
            invalidateSimulationRecord();
            return true;
        }
        const simulation = await handleEnsureSimulation();
        if (!simulation) {
            setAlertData({
                visible: true,
                message: t('recharge.step.beneficiaries-values.error.message'),
                type: 'error',
            });
            return false;
        }
        if (forcePopulate) {
            populateFormData(simulation);
        }
        return true;
    }, [
        currentStep,
        formData.selectedBeneficiariesList?.length,
        handleEnsureSimulation,
        invalidateSimulationRecord,
        populateFormData,
        t,
    ]);
    const handleNext = useCallback(async (resimulate, forcePopulate, nextStep, requiresCardDelivery = formData.requiresCardDelivery) => {
        if (resimulate && !isEmissorFlow) {
            const isSimulationReady = await prepareSimulationForNavigation(forcePopulate);
            if (!isSimulationReady) {
                return;
            }
        }
        const destination = resolveAdvanceStep({
            currentStep,
            isEmissorFlow,
            explicitNextStep: nextStep,
            isEditingFromSummary,
            snapshot: summaryEditSnapshot,
            currentFormData: formData,
            skipDeliveryAddress: shouldSkipDeliveryAddress(isEmissorFlow, requiresCardDelivery),
        });
        if (isEditingFromSummary) {
            setIsEditingFromSummary(false);
            setSummaryEditSnapshot(null);
        }
        setCurrentStep(destination);
    }, [currentStep, formData, isEditingFromSummary, isEmissorFlow, prepareSimulationForNavigation, summaryEditSnapshot]);
    const handleBeneficiariesComplete = useCallback(async (resimulate, forcePopulate, nextStep, requiresCardDelivery) => {
        handlers.setBeneficiariesData({ requiresCardDelivery });
        await handleNext(resimulate, forcePopulate, nextStep, requiresCardDelivery);
    }, [handleNext, handlers]);
    const handleBack = useCallback(() => {
        if (isEditingFromSummary) {
            setIsEditingFromSummary(false);
            setSummaryEditSnapshot(null);
        }
        setCurrentStep(getPreviousRechargeStep(currentStep, isEmissorFlow, shouldSkipDeliveryAddress(isEmissorFlow, formData.requiresCardDelivery)));
    }, [currentStep, formData.requiresCardDelivery, isEditingFromSummary, isEmissorFlow]);
    const handleStepChange = useCallback((step) => {
        if (currentStep === NewRechargeStep.SUMMARY && step !== NewRechargeStep.SUMMARY) {
            setIsEditingFromSummary(true);
            setSummaryEditSnapshot(cloneSummaryEditSnapshot(formData));
        }
        setCurrentStep(step);
    }, [currentStep, formData]);
    const getSimulationState = () => {
        if (isLoading)
            return SimulationState.SIMULATING;
        if (isError)
            return SimulationState.ERROR;
        return SimulationState.SUCCESS;
    };
    const applyEmissorProductDefaults = (voucherTypeId) => {
        const emissorProduct = emissorProducts.find(product => product.productId === voucherTypeId);
        if (!emissorProduct) {
            return;
        }
        const releaseType = emissorProduct.releaseType === ReleaseType.DAILY ? ReleaseType.DAILY : ReleaseType.MONTHLY;
        const isDaily = releaseType === ReleaseType.DAILY;
        const baseAmount = isDaily ? emissorProduct.amountDaily : emissorProduct.baseValue;
        const globalValue = baseAmount ? formatCurrency(baseAmount) : DEFAULT_CURRENCY_VALUE;
        handlers.setBeneficiariesData({
            releaseType,
            globalValue,
            amountDaily: isDaily ? getUnformatedValue(globalValue) : undefined,
        });
    };
    const applyDefaultProductDefaults = (voucherTypeId) => {
        const product = products.find(item => item.voucherType === voucherTypeId);
        if (!product) {
            return;
        }
        handlers.selectPayrollDeduction(product.discountType !== DiscountType.NO_DISCOUNT);
        handlers.selectDiscountType(product.discountType ?? DiscountType.NO_DISCOUNT);
        const releaseType = product.releaseType ?? ReleaseType.MONTHLY;
        let globalValue = DEFAULT_CURRENCY_VALUE;
        if (releaseType === ReleaseType.DAILY && product.amountDaily) {
            globalValue = formatCurrency(product.amountDaily);
        }
        else if (releaseType === ReleaseType.MONTHLY && product.baseValue) {
            globalValue = formatCurrency(product.baseValue);
        }
        handlers.setBeneficiariesData({
            releaseType,
            globalValue,
            amountDaily: releaseType === ReleaseType.DAILY ? getUnformatedValue(globalValue) : undefined,
        });
        if (product.discountValue) {
            const discountValue = product.discountValue.toFixed(2).toString().replace(/\D/g, '');
            handlers.selectDiscountValue(applyMask(discountValue, product.discountType));
        }
        else {
            handlers.selectDiscountValue('');
        }
        if (product.isIndividualDiscount !== undefined) {
            handlers.selectIsIndividualDiscount(product.isIndividualDiscount);
        }
    };
    const onSelectEmissorProductName = (voucherType) => {
        if (formData.voucherTypeId) {
            handlers.setBeneficiariesData({ voucherType: voucherType });
            return;
        }
        handlers.selectVoucherType(voucherType);
    };
    const onUpdateVoucherTypeId = (voucherTypeId) => {
        if (isEmissorFlow) {
            if (formData.voucherTypeId) {
                handlers.setBeneficiariesData({ voucherTypeId });
                return;
            }
            handlers.selectVoucherTypeId(voucherTypeId);
            applyEmissorProductDefaults(voucherTypeId);
            return;
        }
        handlers.selectVoucherTypeId(voucherTypeId);
        applyDefaultProductDefaults(voucherTypeId);
    };
    const handleBusinessDaysChange = useCallback((value) => {
        handlers.setBeneficiariesData({ businessDays: value });
    }, [handlers]);
    const hasSelectedBeneficiaryWithZeroValue = useCallback((simulation) => {
        const voucher = simulation.vouchers?.[0];
        if (!voucher?.beneficiaries?.length)
            return false;
        const activeBeneficiaries = voucher.beneficiaries.filter(b => isActiveStatus(b.statusInProduct));
        if (activeBeneficiaries.length === 0)
            return false;
        if (voucher.releaseType === ReleaseType.DAILY) {
            return activeBeneficiaries.some(b => !b.productDaysQuantity || b.productDaysQuantity === 0);
        }
        return activeBeneficiaries.some(b => !b.productValue || b.productValue === 0);
    }, []);
    const handleOnCompetenceComplete = useCallback(async () => {
        if (isEmissorFlow) {
            await handleNext(false);
            return;
        }
        if (!canKeepCardDeliveryDecision(isEditingFromSummary, summaryEditSnapshot, formData)) {
            handlers.setBeneficiariesData({ requiresCardDelivery: undefined });
        }
        const product = products.find(product => product.voucherTypeDescription === formData.voucherType);
        const isConfigured = product ? isConfiguredVoucher(product) : false;
        const simulation = await handleEnsureSimulation();
        if (!simulation) {
            setAlertData({
                visible: true,
                message: t('recharge.step.beneficiaries-values.error.message'),
                type: 'error',
            });
            return;
        }
        if (hasSelectedBeneficiaryWithZeroValue(simulation)) {
            await handleNext(false);
            return;
        }
        if (isConfigured &&
            !(isInPeriod &&
                !isEmissorFlow &&
                formData.rechargeType === VoucherContractRechargeType.HAPPY_HOLIDAYS_END_OF_THE_YEAR)) {
            await handleNext(true, true, NewRechargeStep.SUMMARY);
            return;
        }
        await handleNext(true);
    }, [
        isEmissorFlow,
        isEditingFromSummary,
        summaryEditSnapshot,
        formData,
        handlers,
        products,
        handleEnsureSimulation,
        hasSelectedBeneficiaryWithZeroValue,
        isInPeriod,
        handleNext,
        t,
    ]);
    const handleSaveAddress = useCallback(async (address) => {
        if (!contractData?.id || !address.deliveryLocationId) {
            throw new Error('Missing contract ID or delivery location ID');
        }
        setIsSavingAddress(true);
        try {
            await updateDeliveryLocation({
                voucherContractId: contractData.id,
                deliveryLocationId: address.deliveryLocationId,
                body: {
                    locationType: address.locationType,
                    country: address.country,
                    address: address.address,
                    addressLine2: address.addressLine2,
                    number: address.number,
                    complement: address.complement,
                    zipCode: address.zipCode,
                    city: address.city,
                    state: address.state,
                    name: address.name,
                },
            }).unwrap();
            if (!isEmissorFlow) {
                await handleSimulate();
            }
        }
        finally {
            setIsSavingAddress(false);
        }
    }, [contractData?.id, updateDeliveryLocation, handleSimulate, isEmissorFlow]);
    const handleSelectAddress = useCallback(async () => {
        if (isEmissorFlow) {
            return;
        }
        setIsSimulatingAddress(true);
        try {
            const simulation = await handleSimulate();
            if (!simulation) {
                throw new Error('Simulation failed');
            }
        }
        finally {
            setIsSimulatingAddress(false);
        }
    }, [handleSimulate, isEmissorFlow]);
    const simulationState = getSimulationState();
    const wizardStepsFlagOn = [
        { title: t('recharge.step.dataRecharge.title') },
        { title: t('recharge.step.products.title') },
        { title: t('recharge.step.wizard.global-settings') },
        { title: t('recharge.step.wizard.beneficiaries') },
        { title: t('recharge.step.wizard.delivery-address') },
    ];
    const resolveActiveStep = (step) => {
        switch (step) {
            case NewRechargeStep.COMPETENCE:
                return 0;
            case NewRechargeStep.PRODUCTS:
                return 1;
            case NewRechargeStep.GLOBAL_SETTINGS:
                return 2;
            case NewRechargeStep.BENEFICIARIES_VALUES:
                return 3;
            case NewRechargeStep.DELIVERY_ADDRESS:
            case NewRechargeStep.SUMMARY:
            case NewRechargeStep.PAYMENT:
                return 4;
            default:
                return 0;
        }
    };
    const headerStepper = isEmissorFlow ? (_jsx(WizardStepper, { steps: wizardStepsFlagOn, activeStep: resolveActiveStep(currentStep) })) : null;
    const currentContent = () => {
        switch (currentStep) {
            case NewRechargeStep.COMPETENCE:
                return (_jsx(Competence, { currentStep: currentStep, formData: formData, onComplete: handleOnCompetenceComplete, onStepChange: handleStepChange, selectCompetence: handlers.selectCompetence, selectVoucherTypeId: onUpdateVoucherTypeId, selectVoucherType: handlers.selectVoucherType, selectRechargeType: handlers.selectRechargeType, setRechargeIdentification: handlers.setRechargeIdentification, "data-testid": 'viewNewRechargePageCompetenceStep', isLoading: isLoading, headerStepper: headerStepper }));
            case NewRechargeStep.PRODUCTS:
                return (_jsx(Products, { currentStep: currentStep, formData: formData, onComplete: () => handleNext(false), onReturn: handleBack, onStepChange: handleStepChange, selectVoucherType: onSelectEmissorProductName, selectVoucherTypeId: onUpdateVoucherTypeId, isLoading: isLoading || (isEmissorFlow && isEmissorProductsLoading), headerStepper: headerStepper, products: emissorProducts, categories: emissorProductCategories, isEmissorFlow: isEmissorFlow }));
            case NewRechargeStep.DELIVERY_ADDRESS:
                if (isEmissorFlow) {
                    return (_jsx(EmissorDeliveryAddress, { contractId: emissorContractId, formData: formData, onComplete: () => handleNext(false), onReturn: handleBack, onSaveAddress: address => handlers.setBeneficiariesData({ emissorDeliveryAddress: address }), onSaveSuccess: () => {
                            setAlertData({
                                visible: true,
                                message: t('recharge.deliveryAddress.success'),
                                type: 'success',
                            });
                        }, onSaveError: () => {
                            setAlertData({
                                visible: true,
                                message: t('recharge.deliveryAddress.error'),
                                type: 'error',
                            });
                        }, isLoading: isLoading, headerStepper: headerStepper }));
                }
                return (_jsx(DeliveryAddress, { deliveryLocation: simulationResult?.deliveryLocation, formData: formData, onComplete: handleNext, onReturn: handleBack, onStepChange: handleStepChange, onSaveAddress: handleSaveAddress, onSelectAddress: handleSelectAddress, onUpdateAddressSuccess: () => {
                        setAlertData({
                            visible: true,
                            message: t('recharge.deliveryAddress.success'),
                            type: 'success',
                        });
                    }, onUpdateAddressError: () => {
                        setAlertData({
                            visible: true,
                            message: t('recharge.deliveryAddress.error'),
                            type: 'error',
                        });
                    }, isSavingAddress: isSavingAddress, isSimulating: isSimulatingAddress, isLoading: isLoading, "data-testid": 'viewNewRechargePageDeliveryAddressStep', headerStepper: headerStepper }));
            case NewRechargeStep.GLOBAL_SETTINGS:
                return (_jsx(GlobalSettings, { currentStep: currentStep, formData: formData, isLoading: isLoading, onComplete: handleNext, onReturn: handleBack, onStepChange: handleStepChange, onSelectCreditDate: handlers.selectCreditDate, onPayrollDeductionChange: handlers.selectPayrollDeduction, onDiscountTypeChange: handlers.selectDiscountType, onDiscountValueChange: handlers.selectDiscountValue, onUpdateBeneficiariesData: handlers.setBeneficiariesData, products: globalSettingsProducts, isDiscountEnabled: isDiscountEnabled, "data-testid": 'viewNewRechargePageGlobalSettingsStep', headerStepper: headerStepper }));
            case NewRechargeStep.BENEFICIARIES_VALUES:
                if (isEmissorFlow) {
                    return (_jsx(EmissorBeneficiariesValues, { contractId: emissorContractId, selectedProduct: selectedEmissorProduct, formData: formData, onUpdateBeneficiariesData: handlers.setBeneficiariesData, onComplete: handleNext, onReturn: handleBack, onStepChange: handleStepChange, simulationState: simulationState, isBeneficiaryDiscountEnabled: isDiscountEnabled && (formData.payrollDeduction ?? false), headerStepper: headerStepper }));
                }
                return (_jsx(BeneficiariesValues, { formData: formData, onUpdateBeneficiariesData: handlers.setBeneficiariesData, onComplete: handleBeneficiariesComplete, onReturn: handleBack, onStepChange: handleStepChange, simulationState: simulationState, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, "data-testid": 'viewNewRechargePageBeneficiariesValuesStep', headerStepper: headerStepper }));
            case NewRechargeStep.PAYMENT:
                return (_jsx(Payment, { currentStep: currentStep, formData: formData, onComplete: handleNext, onReturn: handleBack, onStepChange: handleStepChange, "data-testid": 'viewNewRechargePagePaymentStep' }));
            case NewRechargeStep.SUMMARY:
            default:
                if (isEmissorFlow) {
                    return (_jsx(EmissorSummary, { contractId: emissorContractId, selectedProduct: selectedEmissorProduct, formData: formData, onStepChange: handleStepChange }));
                }
                return (_jsx(Summary, { formData: formData, simulationResult: simulationResult, onComplete: handleNext, onStepChange: handleStepChange, onUpdateFormData: handlers.setBeneficiariesData, onBusinessDaysChange: handleBusinessDaysChange, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, "data-testid": 'viewNewRechargePageSummaryStep' }));
        }
    };
    if (isRepeatInitializing) {
        return (_jsxs("div", { className: styles.initialLoadingPage, "data-testid": 'viewNewRechargePageInitialLoadingPage', children: [_jsx("h1", { className: styles.pageTitle, children: t('benefits.home.title') }), _jsx(ContentLayout, { className: styles.initialLoadingContent, noMaxWidth: true, children: _jsxs("div", { className: styles.loadingContainer, "data-testid": 'viewNewRechargePageInitialLoading', children: [_jsx("span", { className: styles.loadingIcon, children: _jsx(Loading, { size: 'small' }) }), _jsx("p", { className: styles.loadingText, children: t('recharge.repeat.initial-processing') })] }) })] }));
    }
    if (repeatInitializationError) {
        return _jsx(Navigate, { to: '/beneficios', replace: true, state: { showRepeatRechargeError: true } });
    }
    return (_jsxs("div", { className: styles.rechargerPageContainer, "data-testid": 'viewNewRechargePageContainer', children: [currentContent(), alertData.visible && alertData.message && (_jsx(Alert, { message: alertData.message, appearance: alertData.type === 'error' ? 'error' : 'success', alertType: 'toast', density: 'compact', duration: 8, alignItems: 'center', hideCloseButton: false, className: styles.alertMessage, testIdIdentifier: 'NewRechargePageAlert', onClose: () => {
                    setAlertData({ visible: false });
                } }))] }));
}
export function NewRechargePage() {
    return (_jsx(WithContractGuard, { children: _jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(NewRechargePageContent, {}) }) }));
}
